import type { DateType } from '@polyseme/utils/date/date-utils';
import type { VariantProps } from 'class-variance-authority';

import type { DataAttributesProps } from '../../utils/types';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { dayjsDate, formatDate } from '@polyseme/utils/date/date-utils';
import { cva } from 'class-variance-authority';

import { Button, type ButtonProps } from '../../ui';
import { Badge, type BadgeProps } from '../badge';
import { Icon } from '../icons';
import { Tooltip } from '../tooltip';
import { Typography } from '../typography';
import { READ_STATUSES } from './common';

/**
 * Formats a date as a relative time string (e.g., "il y a 5 minutes", "il y a 2 heures")
 * Uses dayjs relativeTime plugin with French locale
 * Falls back to formatted date (DD MMMM YYYY) for dates older than 7 days
 */
export const formatRelativeTimeOrDate = (date: DateType): string => {
  const dayjsInstance = dayjsDate(date);
  const now = dayjsDate();
  const diffDays = now.diff(dayjsInstance, 'day');

  if (diffDays >= 7) {
    return formatDate(dayjsInstance, 'D MMM.');
  }

  return dayjsInstance.fromNow();
};

const NotificationUnreadIndicator = () => (
  <span className="size-2 rounded-full bg-semantic-info-foreground">
    <span className="sr-only">Unread</span>
  </span>
);

const defaultLabels = {
  markAsRead: 'Marquer comme lue',
  markAsUnread: 'Marquer comme non lue',
  archive: 'Archiver',
  unarchive: 'Désarchiver',
};

type NotificationActionButtonProps = Pick<
  ButtonProps,
  'children' | 'variant'
> & {
  onClick: () => void;
  tooltipContent: string;
};

const NotificationActionButton = ({
  children,
  variant = 'ghost',
  onClick,
  tooltipContent,
}: NotificationActionButtonProps) => (
  <Tooltip content={tooltipContent} position="top-center">
    <Button
      isIcon
      size="sm"
      variant={variant}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </Button>
  </Tooltip>
);

const notificationVariants = cva(
  'relative flex w-full cursor-pointer gap-3 border-b p-4 transition-all',
  {
    variants: {
      variant: {
        pending: 'border-border bg-table-alternate',
        read: 'border-border bg-background',
        archived: 'border-border bg-background',
      },
    },
    defaultVariants: {
      variant: 'read',
    },
  }
);

/**
 * Custom filled check circle icon.
 * Lucide/Shadcn doesn't provide filled variants by default (all icons are outlined).
 * This custom SVG is used specifically for the read notification state.
 */
const CustomCheckIcon = () => (
  <svg
    fill="none"
    height="15"
    viewBox="0 0 15 15"
    width="15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      clipRule="evenodd"
      d="M0 7.33333C0 3.28325 3.28325 0 7.33333 0C11.3834 0 14.6667 3.28325 14.6667 7.33333C14.6667 11.3834 11.3834 14.6667 7.33333 14.6667C3.28325 14.6667 0 11.3834 0 7.33333ZM9.80474 5.5286C10.0651 5.78894 10.0651 6.21105 9.80474 6.4714L7.13807 9.13807C6.87772 9.39842 6.45561 9.39842 6.19526 9.13807L4.86193 7.80474C4.60158 7.54439 4.60158 7.12228 4.86193 6.86193C5.12228 6.60158 5.54439 6.60158 5.80474 6.86193L6.66667 7.72386L8.86193 5.5286C9.12228 5.26825 9.54439 5.26825 9.80474 5.5286Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
);

export type NotificationStatus = NonNullable<
  VariantProps<typeof notificationVariants>['variant']
>;

export const NOTIFICATION_DISPLAY_MODES = ['compact', 'detailed'] as const;
type NotificationDisplayMode = (typeof NOTIFICATION_DISPLAY_MODES)[number];

export type NotificationProps = DataAttributesProps &
  VariantProps<typeof notificationVariants> & {
    className?: string;
    title: string;
    description: string;
    timestamp: DateType;
    status: NotificationStatus;
    badge?: BadgeProps;
    onUpdateStatus: (status: NotificationStatus) => void;
    labels?: typeof defaultLabels;
    displayMode?: NotificationDisplayMode;
  };

export type NotificationItem = Omit<NotificationProps, 'onUpdateStatus'> & {
  id: string;
};

export const Notification = ({
  'data-testid': dataTestId,
  className,
  title,
  description,
  timestamp,
  status,
  badge,
  onUpdateStatus,
  labels,
  displayMode = 'compact',
}: NotificationProps) => {
  const isRead = READ_STATUSES.includes(status);
  const labelsActual = { ...defaultLabels, ...labels };

  return (
    <div
      className={cn(notificationVariants({ variant: status }), className)}
      data-testid={dataTestId}
    >
      <div className="flex flex-1 flex-col gap-2.5">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            {!isRead && <NotificationUnreadIndicator />}
            <Typography as="h4" variant="title-7">
              {title}
            </Typography>
            {!!badge && <Badge {...badge} />}
          </div>

          <Typography variant="body">{description}</Typography>
        </div>

        {displayMode === 'detailed' && (
          <Typography className="text-muted-foreground" variant="caption">
            {dayjsDate(timestamp).format('DD MMMM YYYY à HH[h]mm')}
          </Typography>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end justify-between gap-1">
        <Typography
          as="span"
          className="whitespace-nowrap text-muted-foreground"
          variant="caption"
        >
          {formatRelativeTimeOrDate(timestamp)}
        </Typography>

        <div className="flex gap-3">
          {status !== 'archived' && (
            <>
              {displayMode === 'detailed' && (
                <NotificationActionButton
                  tooltipContent={labelsActual.archive}
                  onClick={() => onUpdateStatus('archived')}
                >
                  <Icon className="text-foreground" name="archive" />
                </NotificationActionButton>
              )}
              <NotificationActionButton
                tooltipContent={
                  isRead ? labelsActual.markAsUnread : labelsActual.markAsRead
                }
                variant="ghost"
                onClick={() => onUpdateStatus(isRead ? 'pending' : 'read')}
              >
                <div className="rounded-full text-primary transition-colors">
                  {isRead ? <CustomCheckIcon /> : <Icon name="circle-dashed" />}
                </div>
              </NotificationActionButton>
            </>
          )}
          {status === 'archived' && (
            <NotificationActionButton
              tooltipContent={labelsActual.unarchive}
              onClick={() => onUpdateStatus('read')}
            >
              <Icon className="text-foreground" name="archive-restore" />
            </NotificationActionButton>
          )}
        </div>
      </div>
    </div>
  );
};
